// Copyright (c) StrangeBee 2023

import React, { ReactElement, useMemo } from 'react';
import Form, { FormInstance } from 'antd/lib/form';
import { DescribeOutput, ObjectTypeV1 as ObjectType } from 'thehive-sdk';
import { useLocalMessage } from '@Helpers/';
import { THForm } from '@Molecules/THForm/THForm';
import { THAutoComplete } from '@Molecules/Inputs/THAutoComplete/THAutoComplete';
import { Space } from 'antd';
import { ICONS } from '@Constants/iconsApp';
import { HTMLColors } from '@Constants/colors';

interface AllFieldProps {
    value?: any;
    onChange?: (value: any) => void;
    form?: FormInstance;
    describeData: DescribeOutput;
    fieldType: string[];
    fieldName: string;
    formItemName: string | Array<string | number>;
    entity: ObjectType | undefined;
    aggregationWarning?: boolean;
    fieldsToExclude?: string[];
    indexTypeToExclude?: string[];
}

export const AllField = ({
    value = '',
    onChange,
    describeData,
    fieldType,
    fieldName,
    entity,
    formItemName,
    form,
    aggregationWarning = true,
    fieldsToExclude,
    indexTypeToExclude,
}: AllFieldProps): ReactElement | null => {
    const i = useLocalMessage();

    const attributes = useMemo(
        () =>
            (entity &&
                describeData &&
                describeData[entity] &&
                describeData[entity]?.attributes
                    ?.filter(({ name, type, indexType, aggregable }) => {
                        if (fieldsToExclude?.includes(name)) {
                            return false;
                        }

                        if (!fieldType.includes(type)) {
                            return false;
                        }

                        if (type === 'date') {
                            return indexType === 'standard';
                        }

                        if (indexType === 'fulltextOnly') {
                            return false;
                        }

                        if (indexTypeToExclude?.includes(indexType) && !aggregable) {
                            return false;
                        }

                        return true;
                    })
                    ?.sort(({ name: nameA }, { name: nameB }) => nameA.localeCompare(nameB))) ||
            [],
        [describeData, entity, fieldType, indexTypeToExclude, fieldsToExclude],
    );

    const field = Form.useWatch(Array.isArray(formItemName) ? formItemName : [formItemName], form);
    const serieField = Form.useWatch(
        Array.isArray(formItemName) ? ['series', ...formItemName] : ['series', formItemName],
        form,
    );

    const helpMessage = useMemo(() => {
        const attribute = attributes?.find(({ name }) => name === field || name === serieField);

        if (!attribute) {
            return undefined;
        }

        if (aggregationWarning) {
            if (!attribute.aggregable || (attribute.aggregable && attribute.indexType === 'none')) {
                return i('app.dashboard.widget.create.form.performance');
            }
        }

        if (formItemName === 'periodDateField') {
            return i('dashboard.period-date');
        }

        return undefined;
    }, [formItemName, field, attributes, i, serieField, aggregationWarning]);

    if (!entity || !describeData[entity]) {
        return null;
    }

    return (
        <THForm.Item
            name={formItemName}
            label={i(`app.dashboard.widget.create.form.${fieldName}.label`)}
            rules={[
                {
                    required: true,
                    message: i(`app.dashboard.widget.create.form.${fieldName}.required`),
                    validator: (_, value) => {
                        if (!attributes?.find(({ name }) => name === value)) {
                            return Promise.reject();
                        }

                        return Promise.resolve();
                    },
                },
            ]}
            help={helpMessage}
        >
            <THAutoComplete
                value={value}
                onChange={onChange}
                placeholder={i(`app.dashboard.widget.create.form.${fieldName}.placeholder`)}
            >
                {attributes?.map(({ aggregable, name, indexType }) => (
                    <THAutoComplete.Option key={name} value={name}>
                        <Space>
                            {aggregationWarning && (!aggregable || (aggregable && indexType === 'none')) && (
                                <ICONS.WarningTwoTone twoToneColor={HTMLColors.RED} />
                            )}
                            {name}
                        </Space>
                    </THAutoComplete.Option>
                ))}
            </THAutoComplete>
        </THForm.Item>
    );
};
